import { ReactNode, createContext, useContext } from 'react';
import Term from './Term';
import { GLOSSARY, type GlossaryKey } from '../../lib/glossary';

/** True inside a PanelCard that renders its child cards flat (see `flatChildren`). */
export const FlatCards = createContext(false);

interface Props {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** `filter` is the design `.fcard` (18px 22px padding) for filter rows above a details card. */
  size?: 'default' | 'compact' | 'filter';
  /** Glossary key or inline text explained on hover/focus of the title. */
  help?: GlossaryKey | string;
  /** Header row alignment: `end` is the design `.th2` (actions sit on the title block's baseline). */
  align?: 'center' | 'end';
  /**
   * Render PanelCards nested inside this one as flat headed blocks (no border, fill, shadow or
   * padding), so a details card can host tab bodies built from cards without card-in-card chrome.
   */
  flatChildren?: boolean;
  'aria-label'?: string;
}

const PAD = { default: 'p-[22px]', compact: 'p-4', filter: 'px-[22px] py-[18px]' } as const;

export default function PanelCard({ title, subtitle, actions, children, className = '', size = 'default', help, align = 'center', flatChildren = false, 'aria-label': ariaLabel }: Props) {
  const flat = useContext(FlatCards);
  const chrome = flat ? '' : `rounded-[20px] border border-line bg-surface shadow-card ${PAD[size]}`;
  const body = flatChildren && !flat ? <FlatCards.Provider value>{children}</FlatCards.Provider> : children;
  return (
    <section aria-label={ariaLabel} data-flat={flat || undefined} className={`min-w-0 ${chrome} ${className}`}>
      {(title || actions) && (
        <div className={`mb-[18px] flex flex-wrap justify-between gap-3 ${align === 'end' ? 'items-end' : 'items-center'}`}>
          <div className="min-w-0">
            {typeof title === 'string' ? (
              <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">
                {help ? <Term {...(help in GLOSSARY ? { k: help as GlossaryKey } : { def: help })}>{title}</Term> : title}
              </h2>
            ) : title}
            {subtitle && <p className="mt-1 text-[13px] text-muted">{subtitle}</p>}
          </div>
          {actions && <div className="flex max-w-full flex-none flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {body}
    </section>
  );
}
